import React from "react";
import { Polyline } from "react-leaflet";
import { offsetPolyline } from "../model/helpers/polyline-offset";
import { RouteSegment } from "../model/helpers/route-visualization-builder";
import { PolylineArrows } from "./PolylineArrows";

// Discrete zoom levels to prevent excessive re-renders during smooth zooming
export type ZoomLevelCategory = "low" | "medium" | "high";

export const getZoomCategory = (zoom: number): ZoomLevelCategory => {
  if (zoom >= 16) return "high";
  if (zoom >= 13) return "medium";
  return "low";
};

interface RouteSegmentsLayerProps {
  segments: RouteSegment[];
  zoomCategory: ZoomLevelCategory;
}

export const RouteSegmentsLayer = React.memo(
  ({ segments, zoomCategory }: RouteSegmentsLayerProps) => {
    // Calculate styles based on discrete category, not continuous float zoom
    const borderWidth = zoomCategory === "high" ? 2 : zoomCategory === "medium" ? 4 : 6;
    const lineOffset = zoomCategory === "high" ? 5 : zoomCategory === "medium" ? 25 : 100;

    return (
      <>
        {segments.map((segment, idx) => {
          const offsetPath = offsetPolyline(segment.path, lineOffset);
          return (
            <React.Fragment key={`segment-group-${idx}`}>
              {/* White border (rendered first, appears below) */}
              <Polyline
                key={`segment-border-${idx}`}
                positions={offsetPath}
                color="#FFFFFF"
                dashArray={segment.dashArray}
                weight={segment.weight + borderWidth}
                opacity={1}
                lineCap="round"
                lineJoin="round"
              />
              {/* Colored line (rendered second, appears on top) */}
              <Polyline
                key={`segment-core-${idx}`}
                positions={offsetPath}
                color={segment.color}
                dashArray={segment.dashArray}
                weight={segment.weight}
                opacity={segment.opacity}
                lineCap="round"
                lineJoin="round"
              />
              {/* Direction arrows - only for solid lines (not dashed) */}
              {!segment.dashArray && (
                <PolylineArrows
                  key={`segment-arrows-${idx}`}
                  positions={offsetPath}
                  color={segment.color}
                  weight={segment.weight}
                />
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  },
);

RouteSegmentsLayer.displayName = "RouteSegmentsLayer";
